/**
 * Step N — Review the ad before submit.
 *
 * Shows the final payload as labeled rows + a creative preview card.
 * When the wizard launched in `existing` mode (skipped the Creative
 * step), `AdvantagePlusCreativePanel` renders at the top here so the
 * user can still set Advantage+ Creative on an existing-creative ad.
 *
 * `onEditStep` jumps the wizard back to a previous step's index. The
 * wizard owns step ordering so this component takes a numeric index.
 */
import React from 'react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { CroppedThumbnail } from '@/components/media/CroppedThumbnail';
import type {
  MetaAdCreative,
  MetaAdCreateStatus,
  MetaAdvantagePlusCreativeSpec,
  MetaCreativeSource,
} from '@/services/api/metaAds';
import type { PendingTargeted } from '@/types/media';

import {
  AD_TYPE_LABELS,
  CALL_TO_ACTION_OPTIONS,
  META_PLACEMENT_LABELS,
  STATUS_OPTIONS,
} from '../../constants/metaAdMatrix';
import { AdvantagePlusCreativePanel } from './AdvantagePlusCreativePanel';

/** Enum → human label lookups. The matrix `*_OPTIONS` arrays are the
 *  authoritative label source (same arrays the dropdowns / radios
 *  render), so building these maps from them keeps the review surface
 *  in lockstep with the editing surface. Each falls back to the raw
 *  enum value if a future entry isn't yet in the matrix. */
const CTA_LABELS: Record<string, string> = Object.fromEntries(
  CALL_TO_ACTION_OPTIONS.map((opt) => [opt.value, opt.label]),
);
const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.map((opt) => [opt.value, opt.label]),
);

/** PAUSED reads as a "waiting" state (the ad won't serve yet) and
 *  ACTIVE as "in flight" — map to the Badge variants that read at
 *  a glance. Falls back to `secondary` (gray) for any future status
 *  whose semantics aren't yet known. */
const STATUS_BADGE_VARIANT: Record<
  MetaAdCreateStatus,
  'warning' | 'success'
> = {
  PAUSED: 'warning',
  ACTIVE: 'success',
};

/** Map picker-side `MetaAdCreativeKind` ('image' | 'video' | 'carousel'
 *  | 'unknown') to the wizard-side ad-type key ('link' | 'video' |
 *  'carousel') that `AD_TYPE_LABELS` is keyed by. `'image'` and the
 *  `'unknown'` fallback both map to 'link' because the wizard's "image
 *  ad" variant uses Meta's `link_data` shape under the hood. Without
 *  this mapping, `AD_TYPE_LABELS[kind]` returns undefined for the
 *  (common) `kind === 'image'` case and the next `.title` access
 *  crashes the Review step (Copilot caught this on round 1 of PR
 *  #183).
 */
const KIND_TO_AD_TYPE: Record<
  'image' | 'video' | 'carousel' | 'unknown',
  'link' | 'video' | 'carousel'
> = {
  image: 'link',
  video: 'video',
  carousel: 'carousel',
  unknown: 'link',
};

export interface AdReviewStepProps {
  adName: string;
  status: MetaAdCreateStatus;
  adSetId: string;
  pageId: string;
  instagramActorId: string;
  creativeSource: MetaCreativeSource;
  /** When mode === 'existing', the picker preview. Used to render a tile
   *  showing the chosen creative. */
  pickedCreative: MetaAdCreative | null;
  /** When mode === 'new', the PendingTargeted from the in-wizard image
   *  picker. Rendered as a CroppedThumbnail preview here. The signed_url
   *  doesn't exist yet (materialization happens at submit), so we
   *  render from the source MediaImage + cropBox. */
  pendingImage: PendingTargeted | null;
  advantagePlusCreative: MetaAdvantagePlusCreativeSpec;
  onChangeAdvantagePlusCreative: React.Dispatch<
    React.SetStateAction<MetaAdvantagePlusCreativeSpec>
  >;
  /** Human-readable name of the picked lead form. Rendered on the
   *  creative row when the parent objective is OUTCOME_LEADS. `null`
   *  when the picker hasn't resolved a name for the selected id. */
  leadFormName?: string | null;
  /** Wizard step indices to jump back to. -1 to disable a given edit
   *  button (e.g. the Creative step is hidden in existing mode → omit
   *  the Edit Creative button). */
  onEditBasics: () => void;
  onEditCreative?: () => void;
}

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
      <dt className="text-sm text-gray-600">{label}</dt>
      <dd className="text-sm text-gray-900 font-medium text-right">
        {value}
      </dd>
    </div>
  );
}

export const AdReviewStep: React.FC<AdReviewStepProps> = ({
  adName,
  status,
  adSetId,
  pageId,
  instagramActorId,
  creativeSource,
  pickedCreative,
  pendingImage,
  advantagePlusCreative,
  onChangeAdvantagePlusCreative,
  leadFormName = null,
  onEditBasics,
  onEditCreative,
}) => {
  const isExistingMode = creativeSource.mode === 'existing';
  const newSpec = creativeSource.mode === 'new' ? creativeSource.spec : null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Review and create
        </h3>
        <p className="text-sm text-gray-600">
          Confirm the details. The ad will be created in{' '}
          <strong>{status}</strong> status — Meta will run policy review
          before it serves.
        </p>
      </div>

      {/* In existing-creative mode the Creative step is skipped, so the
          Advantage+ panel renders here. In new-creative mode it already
          rendered at the top of LinkCreativeStep. */}
      {isExistingMode && (
        <AdvantagePlusCreativePanel
          value={advantagePlusCreative}
          onChange={onChangeAdvantagePlusCreative}
        />
      )}

      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-base font-semibold text-gray-900">Basics</h4>
          <Button variant="ghost" onClick={onEditBasics}>
            Edit
          </Button>
        </div>
        <dl>
          <Row label="Ad name" value={adName} />
          <Row
            label="Initial status"
            value={
              <Badge variant={STATUS_BADGE_VARIANT[status] ?? 'secondary'}>
                {STATUS_LABELS[status] ?? status}
              </Badge>
            }
          />
          <Row label="Ad set" value={adSetId} />
          <Row label="Facebook Page" value={pageId} />
          <Row
            label="Instagram actor"
            value={instagramActorId ? instagramActorId : '— (Facebook only)'}
          />
        </dl>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-base font-semibold text-gray-900">
            Creative
          </h4>
          {onEditCreative && !isExistingMode && (
            <Button variant="ghost" onClick={onEditCreative}>
              Edit
            </Button>
          )}
        </div>

        {isExistingMode && pickedCreative && (
          <div className="flex gap-4">
            {pickedCreative.thumbnail_url || pickedCreative.image_url ? (
              <img
                src={
                  pickedCreative.thumbnail_url ?? pickedCreative.image_url ?? ''
                }
                alt={pickedCreative.creative_name ?? 'Existing creative'}
                className="w-24 h-24 object-cover rounded"
              />
            ) : (
              <div className="w-24 h-24 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">
                no preview
              </div>
            )}
            <div className="flex-1 space-y-1">
              <div className="text-sm font-medium text-gray-900">
                {pickedCreative.creative_name ?? 'Existing creative'}
              </div>
              <div className="text-xs text-gray-500">
                Creative ID {pickedCreative.creative_id} ·{' '}
                {AD_TYPE_LABELS[KIND_TO_AD_TYPE[pickedCreative.kind]].title}
              </div>
            </div>
          </div>
        )}

        {newSpec && (
          <>
            {pendingImage && (
              <div className="mb-4 flex items-start gap-4">
                <div className="w-32 h-32 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                  <CroppedThumbnail
                    source={pendingImage.source}
                    cropBox={pendingImage.cropBox}
                    className="w-full h-full"
                  />
                </div>
                <div className="text-xs text-gray-500 pt-1">
                  <div className="font-medium text-gray-700">
                    {pendingImage.source.original_filename ??
                      pendingImage.source.filename}
                  </div>
                  <div className="mt-1">
                    Placement:{' '}
                    {META_PLACEMENT_LABELS[pendingImage.placementKey] ??
                      pendingImage.placementKey}
                  </div>
                  <div className="mt-1 italic">
                    Uploaded to Meta on submit.
                  </div>
                </div>
              </div>
            )}
            <dl>
              <Row label="Type" value="Image" />
              <Row label="Headline" value={newSpec.headline} />
              <Row label="Primary text" value={newSpec.primary_text} />
              <Row
                label="Description"
                value={newSpec.description ?? '—'}
              />
              <Row
                label="Call-to-action"
                value={
                  CTA_LABELS[newSpec.call_to_action ?? 'LEARN_MORE'] ??
                  (newSpec.call_to_action ?? 'LEARN_MORE')
                }
              />
              {newSpec.lead_gen_form_id && (
                <Row
                  label="Lead form"
                  value={leadFormName ?? newSpec.lead_gen_form_id}
                />
              )}
              <Row
                label="Destination URL"
                value={
                  <span className="truncate max-w-xs inline-block">
                    {newSpec.link_url}
                  </span>
                }
              />
            </dl>
          </>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-base font-semibold text-gray-900 mb-2">
          Optimization
        </h4>
        <dl>
          <Row
            label="Advantage+ Creative"
            value={advantagePlusCreative.enabled ? 'On' : 'Off'}
          />
          {advantagePlusCreative.enabled && (
            <>
              <Row
                label="Text variations"
                value={
                  advantagePlusCreative.text_variations ? 'Allowed' : 'Off'
                }
              />
              <Row
                label="Image enhancements"
                value={
                  advantagePlusCreative.image_variations ? 'Allowed' : 'Off'
                }
              />
            </>
          )}
        </dl>
      </div>
    </div>
  );
};
